import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, from, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { v4 } from 'uuid';

import { environment } from '../../../environments/environment';
import { LOCAL_STORAGE } from '../../app.constants';
import { LoginResponse } from '../../interfaces/models';
import { LoadingService } from '../loading/loading.service';
import { NotificationService } from '../notification/notification.service';
import { UserService } from '../user/user.service';
import { DISABLE_NOTIFICATION } from './disable-notification.constant';

@Injectable({
  providedIn: 'root',
})

export class InterceptorService implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string | null | false> = new BehaviorSubject<string | null | false>(null);

  constructor(
    private loadingService: LoadingService,
    private userService: UserService,
    private notificationService: NotificationService,
  ) {
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {

    return next
      .handle(this.addTokenAndTraceIdToRequest(req, localStorage.getItem(LOCAL_STORAGE.accessToken)))
      .pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse) {
            switch ((<HttpErrorResponse>err).status) {
              case 401:
                this.showError(err);
                if (req.url.includes('/auth/refresh-token')) {
                  return this.logoutAndStop();
                }
                if (localStorage.getItem(LOCAL_STORAGE.refreshToken)) {
                  return this.handle401Error(req, next);
                }
                return this.logoutAndStop();
              case 403:
                this.showError(err);
                return this.logoutAndStop();
            }
          }
          this.showError(err);
          return throwError(err);
        }),
      );
  }

  private addTokenAndTraceIdToRequest(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
    const headers: Record<string, string> = {
      'Trace-Id': v4(),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return request.clone({
      setHeaders: headers,
    });
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(null);

      return this.userService.validateRefreshToken({
        refreshToken: localStorage.getItem(LOCAL_STORAGE.refreshToken),
      })
        .pipe(
          switchMap((user: LoginResponse) => {
            if (user) {
              this.userService.setTokens(user);
              this.tokenSubject.next(user.accessToken);
              return next.handle(this.addTokenAndTraceIdToRequest(request, user.accessToken));
            }
            this.tokenSubject.next(false);
            return this.logoutAndStop();
          }),
          catchError(() => {
            this.tokenSubject.next(false);
            return this.logoutAndStop();
          }),
          finalize(() => {
            this.isRefreshingToken = false;
          }),
        );
    }
    return this.tokenSubject
      .pipe(
        filter(token => token != null),
        take(1),
        switchMap((token) => {
          if (token === false) {
            return this.logoutAndStop();
          }
          return next.handle(this.addTokenAndTraceIdToRequest(request, token));
        }));
  }

  private showError(err: HttpErrorResponse): void {
    const path = err && err.url ? err.url.replace(environment.api, '') : '';
    if (!DISABLE_NOTIFICATION.includes(path) && !path.includes('/car/vin')) {
      const message = err && err.error && err.error.message ? err.error.message : '';
      this.notificationService.error(message);
    }
    this.loadingService.stopLoading();
  }

  private logoutAndStop(): Observable<never> {
    return from(this.userService.logout()).pipe(switchMap(() => EMPTY));
  }

}
